import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import type { MotivoNaoEmite } from "@/api/contrato";
import { useVinculosDoBeneficiario } from "@/api/consultas";
import { Esqueleto } from "@/componentes/Esqueleto";
import { FaixaDeErro } from "@/componentes/FaixaDeErro";
import { NaoEncontrado } from "@/componentes/NaoEncontrado";
import { PainelLateral } from "@/componentes/PainelLateral";
import { rotaDaAplicacao, rotaDoCedente } from "@/rotas";
import { useSessao } from "@/sessao/useSessao";
import { MOTIVO_DE_NAO_EMITIR, ROTULO_DA_MODALIDADE } from "@/telas/sistemas/cedente";
import { DetalheDoCedente } from "@/telas/sistemas/DetalheDoCedente";
import { useCedenteDaRota } from "./useCedenteDaRota";

/**
 * O motivo do par em texto, na linha e no painel.
 *
 * `title` não chega ao teclado nem ao toque, e "por que não emito?" é a pergunta destas
 * telas. A suspensão já está escrita na situação, e a falta de contrato, na linha e na
 * seção Contrato: repetir seria dizer a mesma coisa duas vezes na mesma linha.
 */
export function MotivoDoPar({ motivo }: { motivo: MotivoNaoEmite }) {
  if (motivo === null || motivo === "vinculo_suspenso" || motivo === "vinculo_sem_contrato") {
    return null;
  }
  return <span className="text-[12px] text-falha">{MOTIVO_DE_NAO_EMITIR[motivo]}</span>;
}

/*
 * `/cedentes/:id/aplicacoes/:sistemaId`: o vínculo e, em `por_cedente`, o contrato do
 * par, com o mesmo `DetalheDoCedente` da aba Cedentes da aplicação.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ O VÍNCULO VEM DA LISTA DO CEDENTE, E NÃO DA LISTA DA APLICAÇÃO.          │
 * │                                                                          │
 * │ `GET /sistemas/{id}/vinculos` para em 200 linhas, em ordem de razão      │
 * │ social: numa aplicação grande, esta clínica pode não estar ali, e o      │
 * │ painel diria que um vínculo vigente não existe. A lista do cedente é a   │
 * │ mesma consulta da aba, sem requisição a mais, e toda escrita do vínculo  │
 * │ ou do contrato já a invalida.                                            │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * Fechar navega para a aba levando o `state`, para o "« voltar" da página continuar
 * sabendo de que lista o operador veio.
 */
export function DetalheDoVinculo() {
  const { id, pessoa, consulta } = useCedenteDaRota();
  const { sistemaId } = useParams();
  const { cliente } = useSessao();
  const navegar = useNavigate();
  const estado: unknown = useLocation().state;

  const vinculos = useVinculosDoBeneficiario(cliente, id);
  const vinculo = vinculos.data?.vinculos.find((item) => item.sistema_id === sistemaId);
  const inexistente = vinculos.data !== undefined && vinculo === undefined;
  const falha = vinculos.error ?? consulta.error;

  function fechar() {
    void navegar(rotaDoCedente(id, "aplicacoes"), { state: estado });
  }

  if (vinculo === undefined || pessoa === undefined) {
    return (
      <PainelLateral
        aberto
        aoFechar={fechar}
        descricao="Detalhe do vínculo"
        titulo={
          inexistente || falha !== null ? (
            <span className="font-semibold text-texto">Aplicação</span>
          ) : (
            <Esqueleto className="h-5 w-40" />
          )
        }
      >
        <div aria-busy={!inexistente && falha === null} className="p-4">
          {inexistente ? (
            <NaoEncontrado
              texto="Vínculo não encontrado."
              para={rotaDoCedente(id, "aplicacoes")}
              rotuloDoLink="Aplicações"
            />
          ) : falha !== null ? (
            <FaixaDeErro erro={falha} />
          ) : (
            <Esqueleto className="h-40" />
          )}
        </div>
      </PainelLateral>
    );
  }

  return (
    <PainelLateral
      aberto
      aoFechar={fechar}
      descricao={`Vínculo de ${pessoa.razao_social} com ${vinculo.sistema_nome}`}
      titulo={
        <div className="flex min-w-0 flex-col gap-1">
          <Link
            to={rotaDaAplicacao(vinculo.sistema_id)}
            className="truncate text-titulo font-semibold text-texto underline-offset-2 hover:underline"
          >
            {vinculo.sistema_nome}
          </Link>
          <span
            className={`dados truncate text-[12px] ${
              vinculo.status === "suspenso" ? "text-transito" : "text-texto-2"
            }`}
          >
            {/* Estado normal não se anuncia. */}
            {vinculo.status === "ativo" ? (
              <span className="sr-only">ativo · </span>
            ) : (
              `${vinculo.status} · `
            )}
            cobrança {ROTULO_DA_MODALIDADE[vinculo.modalidade_cobranca]}
          </span>
          <MotivoDoPar motivo={vinculo.motivo_nao_emite} />
        </div>
      }
    >
      <div className="p-4">
        <DetalheDoCedente
          sistema={{
            id: vinculo.sistema_id,
            nome: vinculo.sistema_nome,
            cobranca: { modalidade_cobranca: vinculo.modalidade_cobranca },
          }}
          cedente={{ ...vinculo, pessoa_id: id, razao_social: pessoa.razao_social }}
        />
      </div>
    </PainelLateral>
  );
}
